/**
 * Lift2Fit — the programme rendered as an interface, never a grid of cells.
 *
 * Shared by the coach dashboard's read-only panel and the one-time plan result:
 * day cards, labelled superset blocks, per-side / measure / tempo chips and the
 * same formatter The Grid uses, so the two can never disagree.
 */
import type { UnitSystem } from '@lift2fit/shared-types';
import type { GeneratedPlan, PlanDay, PlanExercise } from '@/lib/api';
import { cn } from '@/lib/format';
import { formatPrescription, formatReps, type PrescriptionLine } from '@/lib/prescription';
import { StatusChip } from './components';

/** Adapt a contract exercise to the shared prescription vocabulary. */
export function toPrescriptionLine(exercise: PlanExercise): PrescriptionLine {
  const distance = exercise.measure === 'distance_m' ? (exercise.repMax ?? exercise.repMin) : null;
  const duration = exercise.measure === 'time_s' ? (exercise.repMax ?? exercise.repMin) : null;
  return {
    supersetGroup: exercise.supersetGroup,
    prescribedSets: exercise.sets,
    repMin: exercise.measure === 'reps' || exercise.measure === 'amrap' ? exercise.repMin : null,
    repMax: exercise.measure === 'reps' || exercise.measure === 'amrap' ? exercise.repMax : null,
    measure: exercise.measure,
    perSide: exercise.perSide,
    isBodyweight: exercise.targetWeightKg === null && exercise.percent1rm === null && /bodyweight|band/i.test(exercise.equipment),
    tempo: exercise.tempo,
    targetRpe: exercise.targetRpe,
    percent1rm: exercise.percent1rm,
    targetWeightKg: exercise.targetWeightKg,
    targetDistanceM: distance,
    targetDurationS: duration,
    restSeconds: exercise.restSeconds,
  };
}

function groupExercises(day: PlanDay): Array<{ group: number | null; exercises: PlanExercise[] }> {
  const groups = new Map<number | null, PlanExercise[]>();
  for (const exercise of day.exercises) {
    const key = exercise.supersetGroup;
    const list = groups.get(key) ?? [];
    list.push(exercise);
    groups.set(key, list);
  }
  return [...groups.entries()].map(([group, exercises]) => ({ group, exercises }));
}

function groupLabel(group: number | null, size: number): string | null {
  if (group === null || size < 2) return null;
  if (size === 2) return `Superset ${group} — performed back to back`;
  if (size === 3) return `Tri-set ${group} — performed back to back`;
  return `Giant set ${group} — performed back to back`;
}

function MeasureChips({ exercise }: { exercise: PlanExercise }) {
  return (
    <>
      {exercise.perSide ? <StatusChip tone="neutral">Per side</StatusChip> : null}
      {exercise.measure === 'distance_m' ? <StatusChip tone="neutral">Distance</StatusChip> : null}
      {exercise.measure === 'time_s' ? <StatusChip tone="neutral">Timed</StatusChip> : null}
      {exercise.measure === 'amrap' ? <StatusChip tone="neutral">AMRAP</StatusChip> : null}
      {exercise.tempo ? <StatusChip tone="neutral">Tempo {exercise.tempo}</StatusChip> : null}
      {exercise.targetRpe !== null ? <StatusChip tone="neutral">RPE {exercise.targetRpe}</StatusChip> : null}
      {exercise.percent1rm !== null ? <StatusChip tone="neutral">{exercise.percent1rm}% 1RM</StatusChip> : null}
    </>
  );
}

export function ExerciseRow({ exercise, unit }: { exercise: PlanExercise; unit: UnitSystem }) {
  const line = toPrescriptionLine(exercise);
  return (
    <li className="flex flex-col gap-2 border border-hairline bg-surface p-3.5" aria-label={formatPrescription(line, unit)}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[15px] font-bold text-ink">
          {exercise.movement}
          {exercise.variant ? <span className="ml-2 text-[13px] font-normal text-ink-muted">{exercise.variant}</span> : null}
        </p>
        <p className="l2f-tabular font-mono text-[14px] font-bold text-ink">
          {exercise.sets} × {formatReps(line)}
        </p>
      </div>
      <p className="l2f-tabular font-mono text-[12px] text-ink-muted">{formatPrescription(line, unit)}</p>
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusChip tone="neutral">{exercise.equipment}</StatusChip>
        <MeasureChips exercise={exercise} />
      </div>
      {exercise.notes ? <p className="text-[12px] text-ink-muted">{exercise.notes}</p> : null}
    </li>
  );
}

export function ProgrammeDayCard({ day, unit, className }: { day: PlanDay; unit: UnitSystem; className?: string }) {
  const groups = groupExercises(day);
  return (
    <section className={cn('flex flex-col gap-3 border border-hairline bg-surface p-4', className)} aria-label={day.label}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline pb-3">
        <h3 className="font-display text-[18px] font-black uppercase leading-none text-ink">{day.label}</h3>
        <div className="flex flex-wrap gap-1.5">
          {day.focus.map((focus) => (
            <StatusChip key={focus} tone="accent">
              {focus}
            </StatusChip>
          ))}
        </div>
      </header>

      <div className="flex flex-col gap-3">
        {groups.map((group, index) => {
          const label = groupLabel(group.group, group.exercises.length);
          return (
            <div key={`${day.dayIndex}-${group.group ?? `single-${index}`}`} className="flex flex-col gap-2">
              {label ? (
                <p className="border-l-2 border-l-accent pl-2 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                  {label}
                </p>
              ) : null}
              <ul className="flex flex-col gap-2">
                {group.exercises.map((exercise, exerciseIndex) => (
                  <ExerciseRow key={`${exercise.movement}-${exerciseIndex}`} exercise={exercise} unit={unit} />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function ProgrammeDays({ plan, unit, className }: { plan: GeneratedPlan; unit: UnitSystem; className?: string }) {
  return (
    <div className={cn('grid gap-4', className)}>
      {plan.days.map((day) => (
        <ProgrammeDayCard key={day.dayIndex} day={day} unit={unit} />
      ))}
    </div>
  );
}
